import React, { useState } from "react";
import { v4 as uuid } from "uuid";
import { ExclamationCircleIcon } from "@heroicons/react/24/solid";
import { classNames } from "components/utils";

interface InputFieldProps {
  /** The label text displayed above the input */
  label: string;
  /** Placeholder text shown when input is empty */
  placeholder?: string;
  /** Current value of the input */
  value: string;
  /** HTML input type (text, email, password, number, etc.) */
  type?: string;
  /** Error message to display below the input */
  errorMessage: string;
  /** Callback fired when input value changes */
  onChange: (v: string) => void;
  /** Callback fired when a key is pressed */
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  /** Additional CSS classes to apply to the container */
  className?: string;
  /** Hide the label visually (still accessible to screen readers) */
  hideLabel?: boolean;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Render as textarea instead of input */
  multiline?: boolean;
  /** Text to display at the end of the input (e.g., "USD", "GB") */
  trailingCharacters?: string;
  /** Show required field indicator (asterisk) */
  required?: boolean;
  /** Show negative symbol prefix for negative values */
  isNegative?: boolean;
  /** data-testid attribute for testing */
  "data-testid"?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (props, ref) => {
    // Generate stable ID once on mount to avoid re-renders
    const [fieldId] = useState(() => `input-${uuid().slice(0, 6)}`);

    const handleInputChange = (
      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
      const newValue = e.target.value;
      props.onChange(newValue);
    };

    const hasError = props.errorMessage?.length > 0;

    const getInputContent = () => {
      if (props.multiline) {
        return (
          <textarea
            id={fieldId}
            name={fieldId}
            rows={3}
            disabled={props.disabled}
            className="block w-full rounded-md border-gray-300 bg-white dark:border-borderPrimary dark:bg-secondaryBg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 shadow-sm focus:border-highlight focus:ring-highlight sm:text-sm"
            placeholder={props.placeholder || "File contents"}
            value={props.value}
            onChange={handleInputChange}
            aria-invalid={hasError ? "true" : undefined}
            aria-describedby={hasError ? `${fieldId}-error` : undefined}
            data-testid={props["data-testid"]}
          />
        );
      }
      return (
        <input
          ref={ref}
          disabled={props.disabled}
          type={props.type || "text"}
          name={fieldId}
          id={fieldId}
          className={classNames(
            "px-2.5 py-2",
            hasError
              ? "block w-full pr-10 border-red-300 dark:border-red-500 bg-white dark:bg-zinc-900 text-red-900 dark:text-red-50 placeholder-red-300 dark:placeholder-red-400 focus:outline-none focus:ring-red-500 focus:border-red-500 sm:text-sm rounded-md"
              : "shadow-sm focus:ring-highlight focus:border-highlight bg-white dark:bg-secondaryBg text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 border-gray-300 dark:border-borderPrimary block w-full sm:text-sm rounded-md",
            props.isNegative ? "pl-6" : "",
          )}
          placeholder={props.placeholder}
          value={props.value}
          onChange={handleInputChange}
          onKeyDown={props.onKeyDown}
          aria-invalid={hasError ? "true" : undefined}
          aria-describedby={hasError ? `${fieldId}-error` : undefined}
          data-testid={props["data-testid"]}
        />
      );
    };

    return (
      <div className={props.className || ""}>
        {!props.hideLabel && (
          <div className="flex flex-row">
            <label
              htmlFor={fieldId}
              className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-200"
            >
              {props.label}
            </label>
            {props.required && (
              <span className="ml-1 text-red-400 leading-5">*</span>
            )}
          </div>
        )}
        {props.hideLabel && props.required && (
          <span className="text-red-400">*</span>
        )}
        <div
          className={`${
            hasError || props.isNegative ? "relative rounded-md shadow-sm" : ""
          }`}
        >
          {props.isNegative && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <span
                className="text-red-500 dark:text-red-400 sm:text-sm font-bold"
                id="negative-prefix"
              >
                –
              </span>
            </div>
          )}
          {getInputContent()}
          {props.trailingCharacters && !hasError && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <span
                className="text-gray-500 dark:text-gray-400 sm:text-sm"
                id={`${fieldId}-trailing`}
              >
                {props.trailingCharacters}
              </span>
            </div>
          )}
          {hasError && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
              <ExclamationCircleIcon
                className="h-5 w-5 text-red-500 dark:text-red-400"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
        {hasError && (
          <p
            className="mt-2 text-sm text-red-600 dark:text-red-400"
            id={`${fieldId}-error`}
          >
            {props.errorMessage}
          </p>
        )}
      </div>
    );
  },
);

InputField.displayName = "InputField";

export default InputField;
